import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import * as offline from "../lib/offline";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Save, Trash2, Loader2, Layers, Truck, MapPin } from "lucide-react";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export function HistoryCalendar({ workspace, onOpenDate, activeDate, projectId }) {
  const now = new Date();
  const [month, setMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [snaps, setSnaps] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveDate, setSaveDate] = useState(todayStr());
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    setLoading(true);
    api
      .get("/snapshots", { params: { projectId } })
      .then((r) => {
        const map = {};
        (r.data.snapshots || []).forEach((s) => (map[s.date] = s));
        setSnaps(map);
      })
      .catch(() => toast.error("Failed to load history"))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [projectId]);

  const saveSnapshot = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/snapshots/${saveDate}`, workspace, { params: { projectId } });
      // keep the offline sync "base" in step so this same-user save isn't seen as an external conflict
      if (data?.savedAt) offline.setBase(projectId, saveDate, data.savedAt);
      toast.success(`Snapshot saved for ${saveDate}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openDate = (date) => onOpenDate(date);

  const removeDate = async (e, date) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the snapshot for ${date}?`)) return;
    try {
      await api.delete(`/snapshots/${date}`, { params: { projectId } });
      toast.success(`Deleted ${date}`);
      refresh();
    } catch {
      toast.error("Delete failed");
    }
  };

  const cells = useMemo(() => {
    const y = month.getFullYear();
    const m = month.getMonth();
    const firstDow = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < firstDow; i++) arr.push(null);
    for (let d = 1; d <= days; d++) arr.push({ day: d, date: `${y}-${pad(m + 1)}-${pad(d)}` });
    return arr;
  }, [month]);

  const shiftMonth = (delta) => setMonth((mm) => new Date(mm.getFullYear(), mm.getMonth() + delta, 1));
  const today = todayStr();
  const savedCount = Object.keys(snaps).length;

  return (
    <div className="bg-[#16181A] border border-[#2E3033]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3033] flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#2560C9]" />
          <h2 className="font-heading font-bold text-white text-sm tracking-tight">HISTORY</h2>
          <span className="text-[10px] font-mono text-[#6E7787] ml-1">{savedCount} saved day{savedCount !== 1 ? "s" : ""}</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="snapshot-date-input"
            type="date"
            value={saveDate}
            onChange={(e) => setSaveDate(e.target.value)}
            className="bg-[#0A0A0A] border border-[#2E3033] px-2 py-1.5 text-white text-xs font-mono focus:border-[#2560C9] focus:outline-none"
          />
          <button
            data-testid="save-snapshot-button"
            onClick={saveSnapshot}
            disabled={saving}
            className="flex items-center gap-1.5 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-xs px-3 py-1.5 transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Snapshot
          </button>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <button data-testid="cal-prev-month" onClick={() => shiftMonth(-1)} className="border border-[#2E3033] hover:bg-[#22252A] text-white p-1.5 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-heading font-bold text-white tracking-tight" data-testid="cal-month-label">
            {MONTHS[month.getMonth()]} {month.getFullYear()}
          </span>
          <button data-testid="cal-next-month" onClick={() => shiftMonth(1)} className="border border-[#2E3033] hover:bg-[#22252A] text-white p-1.5 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {WD.map((d) => (
            <div key={d} className="text-center text-[10px] font-mono uppercase tracking-wider text-[#6E7787] py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((c, i) => {
            if (!c) return <div key={`b-${i}`} />;
            const snap = snaps[c.date];
            const isToday = c.date === today;
            const isActive = c.date === activeDate;
            return (
              <button
                key={c.date}
                data-testid={`cal-day-${c.date}`}
                onClick={() => openDate(c.date)}
                className={`group relative aspect-square border p-1.5 flex flex-col text-left transition-all cursor-pointer ${
                  snap
                    ? "border-[#2560C9]/60 bg-[#22252A] hover:bg-[#2560C9]/20 hover:border-[#2560C9]"
                    : "border-[#2E3033] bg-[#0A0A0A] hover:bg-[#16181A] hover:border-[#2560C9]/40"
                } ${isActive ? "ring-2 ring-[#2560C9]" : isToday ? "ring-1 ring-[#3B75DF]/60" : ""}`}
              >
                <span className={`text-[11px] font-mono ${snap ? "text-white" : "text-[#6E7787]"}`}>{c.day}</span>
                {snap ? (
                  <>
                    <div className="mt-auto space-y-0.5 hidden sm:block">
                      <span className="flex items-center gap-1 text-[8px] font-mono text-[#A0AABF]">
                        <Layers className="w-2.5 h-2.5 text-[#2560C9]" />
                        {snap.panelCount}
                      </span>
                      <span className="flex items-center gap-1 text-[8px] font-mono text-[#A0AABF]">
                        <Truck className="w-2.5 h-2.5 text-[#F59E0B]" />
                        {snap.loadCount}
                      </span>
                    </div>
                    <span className="sm:hidden mt-auto w-1.5 h-1.5 rounded-full bg-[#2560C9]" />
                    <span
                      data-testid={`cal-delete-${c.date}`}
                      onClick={(e) => removeDate(e, c.date)}
                      className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 bg-black/70 hover:bg-[#EF4444] text-white p-0.5 transition-opacity"
                    >
                      <Trash2 className="w-2.5 h-2.5" />
                    </span>
                  </>
                ) : (
                  <span className="mt-auto text-[8px] font-mono uppercase tracking-wider text-[#6E7787] opacity-0 group-hover:opacity-100 transition-opacity">
                    + plan
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-[#6E7787] text-xs font-mono mt-4">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading history…
          </div>
        )}
        <p className="mt-4 text-[11px] font-mono text-[#6E7787] flex items-center gap-1.5">
          <MapPin className="w-3 h-3" /> Click any day — past or future — to open or start that day's plan. Saved days show panel/load counts.
        </p>
      </div>
    </div>
  );
}
